import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch, getToken } from '../api/client';
import { prestadorFetch } from '../contexts/PrestadorAuthContext';

const STORAGE_PREFIX = 'sgc_tarefas_v2';

export function formatDataBr(iso) {
  if (!iso) return '';
  const s = String(iso);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('pt-BR');
}

function toIsoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function filtrarNotas(notas, { busca = '', tagIds = [] }) {
  const q = String(busca || '').trim().toLowerCase();
  const tags = tagIds || [];
  return (notas || []).filter((n) => {
    if (tags.length) {
      const notaTagIds = (n.tags || []).map((t) => t.id);
      if (!tags.some((tid) => notaTagIds.includes(tid))) return false;
    }
    if (!q) return true;
    const inTitle = (n.titulo || '').toLowerCase().includes(q);
    const inContent = (n.conteudo || '').toLowerCase().includes(q);
    const inTagNames = (n.tags || []).some((t) => (t.nome || '').toLowerCase().includes(q));
    const termoHash = q.startsWith('#') ? q.slice(1) : q;
    const esc = termoHash.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const rx = new RegExp(`#${esc}\\b`, 'i');
    const inHashtag = rx.test(n.conteudo || '') || rx.test(n.titulo || '');
    return inTitle || inContent || inTagNames || inHashtag;
  });
}

/**
 * Tarefas + bloco de notas via API (sincronizado no backend por usuário).
 * @param {'isg'|'prestador'|''} modo
 * @param {{ enabled?: boolean }} [options]
 */
export function useTarefas(modo = '', options = {}) {
  const { enabled = true } = options;
  const storageKey = modo ? `${STORAGE_PREFIX}_${modo}` : STORAGE_PREFIX;
  const apiBase = modo === 'prestador' ? '/api/prestador/tarefas' : '/api/tarefas';
  const fetchFn = modo === 'prestador' ? prestadorFetch : apiFetch;

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [tarefas, setTarefas] = useState([]);
  const [listas, setListas] = useState([]);
  const [notas, setNotas] = useState([]);
  const [notasCompartilhadas, setNotasCompartilhadas] = useState([]);
  const [tarefasCompartilhadas, setTarefasCompartilhadas] = useState([]);
  const [notificacoes, setNotificacoes] = useState({ totalNaoLidas: 0, items: [] });
  const [tags, setTags] = useState([]);
  const [listaAtivaId, setListaAtivaId] = useState('all');
  const [filtroNav, setFiltroNav] = useState('todas');
  const [viewMode, setViewMode] = useState('tarefas');
  const [notaAtivaId, setNotaAtivaId] = useState(null);
  const [buscaNotas, setBuscaNotas] = useState('');
  const [filtroTagsNotas, setFiltroTagsNotas] = useState([]);

  const carregar = useCallback(async () => {
    const res = await fetchFn(apiBase);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      const err = new Error(data.error || 'Erro ao carregar tarefas.');
      err.status = res.status;
      throw err;
    }
    return res.json();
  }, [apiBase, fetchFn]);

  const tentarMigrarLocal = useCallback(async () => {
    try {
      const rawTarefas = localStorage.getItem(storageKey);
      const rawListas = localStorage.getItem(`${storageKey}_listas`);
      if (!rawTarefas) return false;
      const parsedTarefas = JSON.parse(rawTarefas);
      if (!Array.isArray(parsedTarefas) || parsedTarefas.length === 0) return false;
      const parsedListas = rawListas ? JSON.parse(rawListas) : [];
      await fetchFn(`${apiBase}/import-local`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tarefas: parsedTarefas, listas: parsedListas })
      });
      localStorage.removeItem(storageKey);
      localStorage.removeItem(`${storageKey}_listas`);
      return true;
    } catch {
      return false;
    }
  }, [apiBase, fetchFn, storageKey]);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (modo !== 'prestador' && !getToken()) {
      setLoading(false);
      setLoadError(null);
      return;
    }

    setLoading(true);
    setLoadError(null);
    try {
      const migrou = await tentarMigrarLocal();
      let data = await carregar();
      if (migrou) data = await carregar();
      setTarefas(data.tarefas || []);
      setTarefasCompartilhadas(data.tarefasCompartilhadas || []);
      setListas(data.listas || []);
      setNotas(data.notas || []);
      setNotasCompartilhadas(data.notasCompartilhadas || []);
      setTags(data.tags || []);
      setNotificacoes(data.notificacoes || { totalNaoLidas: 0, items: [] });
      const todasNotas = [...(data.notas || []), ...(data.notasCompartilhadas || [])];
      setNotaAtivaId((prev) => prev || todasNotas[0]?.id || null);
    } catch (error) {
      console.warn('[useTarefas] Falha ao carregar:', error);
      const msg =
        error?.status === 401
          ? 'Sessão expirada. Faça login novamente.'
          : error?.message || 'Erro ao carregar tarefas.';
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  }, [carregar, enabled, modo, tentarMigrarLocal]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const listaAtiva = listas.find((l) => l.id === listaAtivaId) || listas[0];

  const tarefasFiltradas = useMemo(() => {
    if (filtroNav === 'compartilhadas') {
      return (tarefasCompartilhadas || []).map((t) => ({
        ...t,
        souDono: false,
        compartilhada: true
      }));
    }
    return tarefas.filter((t) => {
      if (filtroNav === 'estrela') return t.estrelada;
      if (filtroNav === 'lista' && listaAtivaId !== 'all') {
        return Number(t.listaId) === Number(listaAtivaId);
      }
      return true;
    });
  }, [tarefas, tarefasCompartilhadas, filtroNav, listaAtivaId]);

  const podeCompartilhar = modo === 'isg';

  const pendentes = tarefasFiltradas.filter((t) => !t.concluida);
  const concluidas = tarefasFiltradas.filter((t) => t.concluida);

  const adicionar = useCallback(
    async ({ titulo, detalhes = '', dataVencimento = null, listaId = listaAtivaId }) => {
      const texto = String(titulo || '').trim();
      if (!texto) return false;
      const lid =
        listaId === 'all' || !listaId ? listas[0]?.id : Number(listaId) || listas[0]?.id;
      const res = await fetchFn(apiBase, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ titulo: texto, detalhes, dataVencimento, listaId: lid })
      });
      if (!res.ok) return false;
      await refresh();
      return true;
    },
    [apiBase, fetchFn, listaAtivaId, listas, refresh]
  );

  const concluir = useCallback(
    async (id) => {
      const t = tarefas.find((x) => x.id === id);
      if (t?.subtarefas?.length) return false;
      await fetchFn(`${apiBase}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ concluida: true })
      });
      await refresh();
    },
    [apiBase, fetchFn, refresh, tarefas]
  );

  const reabrir = useCallback(
    async (id) => {
      await fetchFn(`${apiBase}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ concluida: false })
      });
      await refresh();
    },
    [apiBase, fetchFn, refresh]
  );

  const toggleEstrela = useCallback(
    async (id) => {
      const t = tarefas.find((x) => x.id === id);
      if (!t) return;
      await fetchFn(`${apiBase}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estrelada: !t.estrelada })
      });
      await refresh();
    },
    [apiBase, fetchFn, refresh, tarefas]
  );

  const excluir = useCallback(
    async (id) => {
      await fetchFn(`${apiBase}/${id}`, { method: 'DELETE' });
      await refresh();
    },
    [apiBase, fetchFn, refresh]
  );

  const criarLista = useCallback(
    async (nome) => {
      const texto = String(nome || '').trim();
      if (!texto) return null;
      const res = await fetchFn(`${apiBase}/listas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome: texto })
      });
      if (!res.ok) return null;
      const data = await res.json();
      await refresh();
      setListaAtivaId(data.id);
      setFiltroNav('lista');
      return data;
    },
    [apiBase, fetchFn, refresh]
  );

  const adicionarSubtarefa = useCallback(
    async (tarefaId, titulo) => {
      const texto = String(titulo || '').trim();
      if (!texto) return false;
      const res = await fetchFn(`${apiBase}/${tarefaId}/subtarefas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ titulo: texto })
      });
      if (!res.ok) return false;
      await refresh();
      return true;
    },
    [apiBase, fetchFn, refresh]
  );

  const toggleSubtarefa = useCallback(
    async (tarefaId, subId, concluida) => {
      await fetchFn(`${apiBase}/${tarefaId}/subtarefas/${subId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ concluida })
      });
      await refresh();
    },
    [apiBase, fetchFn, refresh]
  );

  const excluirSubtarefa = useCallback(
    async (tarefaId, subId) => {
      await fetchFn(`${apiBase}/${tarefaId}/subtarefas/${subId}`, { method: 'DELETE' });
      await refresh();
    },
    [apiBase, fetchFn, refresh]
  );

  const adicionarNota = useCallback(async () => {
    const res = await fetchFn(`${apiBase}/notas`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ titulo: 'Nova nota', conteudo: '' })
    });
    if (!res.ok) return null;
    const data = await res.json();
    await refresh();
    setNotaAtivaId(data.id);
    setViewMode('notas');
    return data.id;
  }, [apiBase, fetchFn, refresh]);

  const atualizarNota = useCallback(
    async (notaId, patch) => {
      await fetchFn(`${apiBase}/notas/${notaId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch)
      });
      await refresh();
    },
    [apiBase, fetchFn, refresh]
  );

  const excluirNota = useCallback(
    async (notaId) => {
      await fetchFn(`${apiBase}/notas/${notaId}`, { method: 'DELETE' });
      setNotaAtivaId(null);
      await refresh();
    },
    [apiBase, fetchFn, refresh]
  );

  const criarTag = useCallback(
    async ({ nome, cor }) => {
      const res = await fetchFn(`${apiBase}/notas/tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome, cor })
      });
      if (!res.ok) return null;
      const data = await res.json();
      await refresh();
      return data.id;
    },
    [apiBase, fetchFn, refresh]
  );

  const carregarIndicadores = useCallback(
    async (de, ate) => {
      const qs = new URLSearchParams();
      if (de) qs.set('de', de);
      if (ate) qs.set('ate', ate);
      const res = await fetchFn(`${apiBase}/indicadores?${qs.toString()}`);
      if (!res.ok) throw new Error('Erro ao carregar indicadores.');
      return res.json();
    },
    [apiBase, fetchFn]
  );

  const toggleFiltroTagNota = useCallback((tagId) => {
    setFiltroTagsNotas((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  }, []);

  const todasNotas = useMemo(
    () => [
      ...(notas || []).map((n) => ({ ...n, souDono: n.souDono !== false })),
      ...(notasCompartilhadas || []).map((n) => ({ ...n, souDono: false, compartilhada: true }))
    ],
    [notas, notasCompartilhadas]
  );

  const notasFiltradas = useMemo(() => {
    const base =
      filtroNav === 'compartilhadas'
        ? todasNotas.filter((n) => n.compartilhada)
        : todasNotas.filter((n) => !n.compartilhada);
    return filtrarNotas(base, { busca: buscaNotas, tagIds: filtroTagsNotas });
  }, [todasNotas, buscaNotas, filtroTagsNotas, filtroNav]);

  const listarUsuariosCompartilhaveis = useCallback(async () => {
    const res = await fetchFn(`${apiBase}/usuarios-compartilhaveis`);
    if (!res.ok) return [];
    return res.json();
  }, [apiBase, fetchFn]);

  const compartilharNota = useCallback(
    async (notaId, usuarioId, permissao) => {
      const res = await fetchFn(`${apiBase}/notas/${notaId}/compartilhar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usuarioId, permissao })
      });
      if (!res.ok) throw new Error('Erro ao compartilhar nota.');
      await refresh();
    },
    [apiBase, fetchFn, refresh]
  );

  const atualizarCompartilhamentoNota = useCallback(
    async (notaId, usuarioId, permissao) => {
      const res = await fetchFn(`${apiBase}/notas/${notaId}/compartilhar/${usuarioId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissao })
      });
      if (!res.ok) throw new Error('Erro ao alterar permissão.');
      await refresh();
    },
    [apiBase, fetchFn, refresh]
  );

  const removerCompartilhamentoNota = useCallback(
    async (notaId, usuarioId) => {
      const res = await fetchFn(`${apiBase}/notas/${notaId}/compartilhar/${usuarioId}`, {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error('Erro ao remover compartilhamento.');
      await refresh();
    },
    [apiBase, fetchFn, refresh]
  );

  const compartilharTarefa = useCallback(
    async (tarefaId, usuarioId, permissao) => {
      const res = await fetchFn(`${apiBase}/${tarefaId}/compartilhar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usuarioId, permissao })
      });
      if (!res.ok) throw new Error('Erro ao compartilhar tarefa.');
      await refresh();
    },
    [apiBase, fetchFn, refresh]
  );

  const atualizarCompartilhamentoTarefa = useCallback(
    async (tarefaId, usuarioId, permissao) => {
      const res = await fetchFn(`${apiBase}/${tarefaId}/compartilhar/${usuarioId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissao })
      });
      if (!res.ok) throw new Error('Erro ao alterar permissão.');
      await refresh();
    },
    [apiBase, fetchFn, refresh]
  );

  const removerCompartilhamentoTarefa = useCallback(
    async (tarefaId, usuarioId) => {
      const res = await fetchFn(`${apiBase}/${tarefaId}/compartilhar/${usuarioId}`, {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error('Erro ao remover compartilhamento.');
      await refresh();
    },
    [apiBase, fetchFn, refresh]
  );

  const marcarNotificacaoLida = useCallback(
    async (id) => {
      await fetchFn(`${apiBase}/notificacoes/${id}/lida`, { method: 'PATCH' });
      await refresh();
    },
    [apiBase, fetchFn, refresh]
  );

  const marcarTodasNotificacoesLidas = useCallback(async () => {
    await fetchFn(`${apiBase}/notificacoes/marcar-todas-lidas`, { method: 'POST' });
    await refresh();
  }, [apiBase, fetchFn, refresh]);

  const definirVencimento = useCallback((opcao) => {
    const hoje = new Date();
    if (opcao === 'hoje') return toIsoDate(hoje);
    if (opcao === 'amanha') {
      const amanha = new Date(hoje);
      amanha.setDate(amanha.getDate() + 1);
      return toIsoDate(amanha);
    }
    return null;
  }, []);

  return {
    loading,
    loadError,
    refresh,
    tarefas: tarefasFiltradas,
    tarefasProprias: tarefas,
    pendentes,
    concluidas,
    pendentesCount: pendentes.length,
    concluidasCount: concluidas.length,
    listas,
    listaAtiva,
    listaAtivaId,
    setListaAtivaId,
    filtroNav,
    setFiltroNav,
    viewMode,
    setViewMode,
    notas,
    notasCompartilhadas,
    todasNotas,
    notasFiltradas,
    tarefasCompartilhadas,
    notificacoes,
    podeCompartilhar,
    tags,
    buscaNotas,
    setBuscaNotas,
    filtroTagsNotas,
    setFiltroTagsNotas,
    toggleFiltroTagNota,
    notaAtivaId,
    setNotaAtivaId,
    adicionar,
    concluir,
    reabrir,
    toggleEstrela,
    excluir,
    criarLista,
    definirVencimento,
    adicionarSubtarefa,
    toggleSubtarefa,
    excluirSubtarefa,
    adicionarNota,
    atualizarNota,
    excluirNota,
    criarTag,
    carregarIndicadores,
    listarUsuariosCompartilhaveis,
    compartilharNota,
    atualizarCompartilhamentoNota,
    removerCompartilhamentoNota,
    compartilharTarefa,
    atualizarCompartilhamentoTarefa,
    removerCompartilhamentoTarefa,
    marcarNotificacaoLida,
    marcarTodasNotificacoesLidas,
    refresh
  };
}
